/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Locale, LocaleKeys } from "../../model/locale";
import { ActionKernelType, ActionType } from "../../model/types";
import { interpolateLocaleTemplate } from "../utility";
import type {
  ContentDetailActionIconKind,
  ContentDetailKind,
  ContentDetailSectionId
} from "./contentDetail.types";

/**
 * Every user-facing string on the Content Detail screen, resolved from the
 * locale bundle. The web analogue of Android's `DetailsScreenUiStrings`.
 *
 * Nothing here is resolved at module-eval time: `Locale.setLocale` runs
 * asynchronously during app startup, so a table built at import time would
 * capture an empty language map. Every accessor calls `Locale.getString`
 * on demand instead.
 *
 * The kernel tables below map `ActionKernelType` values to *locale keys*.
 * Many distinct kernels collapse onto the same key — that is the action
 * catalog's design (docs/unity-client-doc/doc/screens/content/actions/
 * 01-actions.content.md and 01-actions.series.md), and the de-duplication
 * in `contentDetailMappers` relies on it.
 */

/** Watch Actions row — 01-actions.content.md "Watch Actions ▸ Planned". */
const WATCH_ACTION_KERNEL_KEYS: Record<string, string> = {
  [ActionKernelType.WatchLiveActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_WATCH_ON_NOW,
  [ActionKernelType.WatchFromMyShowsActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_RECORDING,
  [ActionKernelType.ResumeFromMyShowsActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_RESUME,
  [ActionKernelType.WatchFromSocuActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_CATCHUP,
  [ActionKernelType.WatchFromVodActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_ON_DEMAND,
  [ActionKernelType.WatchFromVodOfferActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_ON_DEMAND,
  [ActionKernelType.WatchVodOfferPreviewActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_PREVIEW,
  [ActionKernelType.WatchVodContentPreviewActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_PREVIEW,
  /**
   * The catalog title for this kernel is "<Provider Name>", but the provider
   * display name lives on `WatchFromProviderAction.provider`, not on the base
   * `Action`/`ActionKernel` shape the mapper sees. Falls back to the generic
   * "Watch" label; the provider-specific caption is rendered separately from
   * `streamingServices` (see CONTENT_WATCH_ON_PROVIDER).
   */
  [ActionKernelType.WatchFromOttActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_WATCH,
  // Legacy in the catalog, but Android ships it as the last Watch pill
  // (`StreamingOptionsDataMapper.addStreamingOptionsAction`).
  [ActionKernelType.StreamingOptionsActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_STREAMING_OPTIONS
};

/**
 * Schedule Actions row — 01-actions.content.md "Schedule Actions ▸ Planned"
 * plus the series/unified-collection additions in 01-actions.series.md
 * (OnePass, multi-OnePass, season/year favorite kernels).
 */
const SCHEDULE_ACTION_KERNEL_KEYS: Record<string, string> = {
  // Content-level (01-actions.content.md)
  [ActionKernelType.RecordAgainActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_RECORD_AGAIN,
  [ActionKernelType.RecordThisShowActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_RECORD,
  [ActionKernelType.RecordThisMovieActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_RECORD,
  [ActionKernelType.RecordThisEpisodeActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_RECORD,
  [ActionKernelType.RecordThisGameActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_RECORD,
  [ActionKernelType.RecordThisMatchActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_RECORD,
  [ActionKernelType.RecordThisRaceActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_RECORD,
  [ActionKernelType.RecordThisEventActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_RECORD,
  [ActionKernelType.ModifyRecordingActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_MODIFY_RECORDING,
  [ActionKernelType.ModifyKeepUntilActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_MODIFY_RECORDING,
  [ActionKernelType.RecoverRecordingActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_RECOVER_RECORDING,
  [ActionKernelType.StopAndDeleteRecordingActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_STOP_AND_DELETE_RECORDING,
  [ActionKernelType.CancelRecordingActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_CANCEL_RECORDING,
  [ActionKernelType.DeleteRecordingActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_DELETE_RECORDING,
  [ActionKernelType.BookmarkThisShowActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_ADD_FAVORITE,
  [ActionKernelType.BookmarkThisMovieActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_ADD_FAVORITE,
  [ActionKernelType.BookmarkThisEpisodeActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_ADD_FAVORITE,
  [ActionKernelType.BookmarkThisGameActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_ADD_FAVORITE,
  [ActionKernelType.BookmarkThisMatchActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_ADD_FAVORITE,
  [ActionKernelType.BookmarkThisRaceActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_ADD_FAVORITE,
  [ActionKernelType.BookmarkThisEventActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_ADD_FAVORITE,
  [ActionKernelType.DeleteBookmarkActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_REMOVE_FAVORITE,
  // Series-level (01-actions.series.md)
  [ActionKernelType.CreateOnePassActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_RECORD_SERIES,
  [ActionKernelType.CreateOnePassActionKernelV2]:
    LocaleKeys.CONTENT_ACTION_LABEL_RECORD_SERIES,
  [ActionKernelType.ModifyOnePassActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_MANAGE_RECORDINGS,
  [ActionKernelType.ModifyOnePassActionKernelV2]:
    LocaleKeys.CONTENT_ACTION_LABEL_MANAGE_RECORDINGS,
  [ActionKernelType.CancelOnePassActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_MANAGE_RECORDINGS,
  [ActionKernelType.CancelOnePassActionKernelV2]:
    LocaleKeys.CONTENT_ACTION_LABEL_MANAGE_RECORDINGS,
  [ActionKernelType.CreateMultiOnePassActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_MANAGE_RECORDINGS,
  [ActionKernelType.ModifyMultiOnePassActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_MANAGE_RECORDINGS,
  [ActionKernelType.CancelMultiOnePassActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_MANAGE_RECORDINGS,
  [ActionKernelType.AddSeasonStreamingVideoActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_ADD_FAVORITE,
  [ActionKernelType.AddYearStreamingVideoActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_ADD_FAVORITE,
  [ActionKernelType.DeleteAllEpisodesActionKernel]:
    LocaleKeys.CONTENT_ACTION_LABEL_MANAGE_RECORDINGS
};

/**
 * Labels for an action whose kernel is in neither table above, keyed by its
 * `actionType` — Android's `ACTION_TYPE_FALLBACK_LABELS`, with the Schedule
 * entries kept on the web catalog labels.
 */
const WATCH_ACTION_TYPE_FALLBACK_KEYS: Record<string, string> = {
  [ActionType.WatchFromProviderAction]: LocaleKeys.CONTENT_ACTION_LABEL_WATCH,
  [ActionType.WatchNowAction]: LocaleKeys.CONTENT_ACTION_LABEL_WATCH,
  [ActionType.PlayRecordingAction]: LocaleKeys.CONTENT_ACTION_LABEL_RECORDING,
  [ActionType.LiveTvAction]: LocaleKeys.CONTENT_ACTION_LABEL_WATCH_ON_NOW,
  [ActionType.WatchUnifiedCollectionAction]:
    LocaleKeys.CONTENT_ACTION_LABEL_WATCH,
  [ActionType.PlayNextRecordingAction]:
    LocaleKeys.CONTENT_ACTION_LABEL_PLAY_NEXT,
  [ActionType.PlayNextVodProviderAction]:
    LocaleKeys.CONTENT_ACTION_LABEL_PLAY_NEXT,
  [ActionType.PlayNextOttProviderAction]:
    LocaleKeys.CONTENT_ACTION_LABEL_PLAY_NEXT
};

const SCHEDULE_ACTION_TYPE_FALLBACK_KEYS: Record<string, string> = {
  [ActionType.ModifyRecordingAction]:
    LocaleKeys.CONTENT_ACTION_LABEL_MODIFY_RECORDING,
  [ActionType.OnePassAction]: LocaleKeys.CONTENT_ACTION_LABEL_RECORD_SERIES,
  [ActionType.MultiOnePassAction]:
    LocaleKeys.CONTENT_ACTION_LABEL_RECORD_SERIES,
  [ActionType.AddSeasonStreamingVideoAction]:
    LocaleKeys.CONTENT_ACTION_LABEL_ADD_FAVORITE,
  [ActionType.AddYearStreamingVideoAction]:
    LocaleKeys.CONTENT_ACTION_LABEL_ADD_FAVORITE,
  [ActionType.DeleteAllAction]:
    LocaleKeys.CONTENT_ACTION_LABEL_MANAGE_RECORDINGS
};

/** Leading glyph per kernel — the catalogs' "Presentation" column. */
const ACTION_KERNEL_ICON_KINDS: Record<string, ContentDetailActionIconKind> = {
  [ActionKernelType.RecordAgainActionKernel]: "record",
  [ActionKernelType.RecordThisShowActionKernel]: "record",
  [ActionKernelType.RecordThisMovieActionKernel]: "record",
  [ActionKernelType.RecordThisEpisodeActionKernel]: "record",
  [ActionKernelType.RecordThisGameActionKernel]: "record",
  [ActionKernelType.RecordThisMatchActionKernel]: "record",
  [ActionKernelType.RecordThisRaceActionKernel]: "record",
  [ActionKernelType.RecordThisEventActionKernel]: "record",
  [ActionKernelType.ModifyRecordingActionKernel]: "record",
  [ActionKernelType.ModifyKeepUntilActionKernel]: "record",
  [ActionKernelType.CancelRecordingActionKernel]: "recordCancel",
  [ActionKernelType.StopAndDeleteRecordingActionKernel]: "stop",
  [ActionKernelType.DeleteRecordingActionKernel]: "delete",
  [ActionKernelType.BookmarkThisShowActionKernel]: "favorite",
  [ActionKernelType.BookmarkThisMovieActionKernel]: "favorite",
  [ActionKernelType.BookmarkThisEpisodeActionKernel]: "favorite",
  [ActionKernelType.BookmarkThisGameActionKernel]: "favorite",
  [ActionKernelType.BookmarkThisMatchActionKernel]: "favorite",
  [ActionKernelType.BookmarkThisRaceActionKernel]: "favorite",
  [ActionKernelType.BookmarkThisEventActionKernel]: "favorite",
  [ActionKernelType.DeleteBookmarkActionKernel]: "favoriteFilled",
  [ActionKernelType.CreateOnePassActionKernel]: "record",
  [ActionKernelType.CreateOnePassActionKernelV2]: "record",
  [ActionKernelType.ModifyOnePassActionKernel]: "record",
  [ActionKernelType.ModifyOnePassActionKernelV2]: "record",
  [ActionKernelType.CancelOnePassActionKernel]: "record",
  [ActionKernelType.CancelOnePassActionKernelV2]: "record",
  [ActionKernelType.CreateMultiOnePassActionKernel]: "record",
  [ActionKernelType.ModifyMultiOnePassActionKernel]: "record",
  [ActionKernelType.CancelMultiOnePassActionKernel]: "record",
  [ActionKernelType.AddSeasonStreamingVideoActionKernel]: "favorite",
  [ActionKernelType.AddYearStreamingVideoActionKernel]: "favorite",
  [ActionKernelType.DeleteAllEpisodesActionKernel]: "record"
};

/** Glyph for an `actionType` fallback (kernel absent from the tables). */
const ACTION_TYPE_ICON_KINDS: Record<string, ContentDetailActionIconKind> = {
  [ActionType.ModifyRecordingAction]: "record",
  [ActionType.OnePassAction]: "record",
  [ActionType.MultiOnePassAction]: "record",
  [ActionType.DeleteAllAction]: "record",
  [ActionType.AddSeasonStreamingVideoAction]: "favorite",
  [ActionType.AddYearStreamingVideoAction]: "favorite"
};

/**
 * Explore section headers. Figma frame `2198-5933` labels the first section
 * "EPISODES", so this uses `content_explore_tab_episodes` rather than
 * Android's `content_explore_tab_episode_guide` ("Episode Guide"); the
 * uppercasing is CSS, not copy.
 */
const SECTION_LABEL_KEYS: Record<ContentDetailSectionId, string> = {
  episodes: LocaleKeys.CONTENT_EXPLORE_TAB_EPISODES,
  upcoming: LocaleKeys.CONTENT_EXPLORE_TAB_UPCOMING,
  cast: LocaleKeys.CONTENT_EXPLORE_TAB_CAST,
  mayAlsoLike: LocaleKeys.CONTENT_EXPLORE_TAB_MAY_ALSO_LIKE
};

const CONTENT_TYPE_LABEL_KEYS: Record<ContentDetailKind, string> = {
  movie: LocaleKeys.CONTENT_TYPE_LABEL_MOVIE,
  series: LocaleKeys.CONTENT_TYPE_LABEL_SERIES,
  episode: LocaleKeys.CONTENT_TYPE_LABEL_EPISODE,
  program: LocaleKeys.CONTENT_TYPE_LABEL_PROGRAM,
  game: LocaleKeys.CONTENT_TYPE_LABEL_GAME
};

const CONTENT_TYPE_MOVIE: ContentDetailKind = "movie";
const CONTENT_TYPE_SERIES: ContentDetailKind = "series";
const CONTENT_TYPE_EPISODE: ContentDetailKind = "episode";
const CONTENT_TYPE_UNIFIED_COLLECTION: string = "unifiedcollection";
const UNIFIED_COLLECTION_ID_PREFIX: string = "tivo:cl.";

/**
 * `seasonOrYear` values below this are "extras" — episodes with neither a
 * season nor a year. Mirrors Android's `EXTRA_SEASON_THRESHOLD`.
 */
export const EXTRA_SEASON_THRESHOLD: number = 0;

/**
 * Above this, a `seasonOrYear` is read as a calendar year rather than a season
 * number. The episode-guide response also carries an explicit `grouping`
 * discriminator; this is the fallback for responses that omit it.
 */
const MIN_YEAR_GROUPING_VALUE: number = 1000;

/**
 * Locale key for a Watch Actions row pill, or `undefined` when the kernel
 * does not belong in that row.
 */
export function getWatchActionLabelKey(kernelType: string): string | undefined {
  return WATCH_ACTION_KERNEL_KEYS[kernelType];
}

/**
 * Locale key for a Schedule Actions row pill, or `undefined` when the kernel
 * does not belong in that row.
 */
export function getScheduleActionLabelKey(
  kernelType: string
): string | undefined {
  return SCHEDULE_ACTION_KERNEL_KEYS[kernelType];
}

/** Watch-row locale key for an `actionType` whose kernel is unrecognized. */
export function getWatchActionTypeFallbackKey(
  actionType: string
): string | undefined {
  return WATCH_ACTION_TYPE_FALLBACK_KEYS[actionType];
}

/** Schedule-row locale key for an `actionType` whose kernel is unrecognized. */
export function getScheduleActionTypeFallbackKey(
  actionType: string
): string | undefined {
  return SCHEDULE_ACTION_TYPE_FALLBACK_KEYS[actionType];
}

/**
 * Leading glyph for an action pill. Every Watch pill is a play glyph; Schedule
 * pills resolve by kernel, then by `actionType`. `undefined` means title only
 * (e.g. Recover Recording).
 */
export function getActionIconKind(
  kernelType: string,
  actionType: string
): ContentDetailActionIconKind | undefined {
  if (
    WATCH_ACTION_KERNEL_KEYS[kernelType] !== undefined ||
    (SCHEDULE_ACTION_KERNEL_KEYS[kernelType] === undefined &&
      WATCH_ACTION_TYPE_FALLBACK_KEYS[actionType] !== undefined)
  ) {
    return "play";
  }
  if (SCHEDULE_ACTION_KERNEL_KEYS[kernelType] !== undefined) {
    return ACTION_KERNEL_ICON_KINDS[kernelType];
  }
  return ACTION_TYPE_ICON_KINDS[actionType];
}

/** Resolved header label for an explore section. */
export function getSectionLabel(sectionId: ContentDetailSectionId): string {
  return Locale.getString(SECTION_LABEL_KEYS[sectionId]);
}

/** Resolved content-type label for the hero type row ("Movie", "Series", …). */
export function getContentTypeLabel(kind: ContentDetailKind): string {
  return Locale.getString(CONTENT_TYPE_LABEL_KEYS[kind]);
}

/** Inputs used to infer the content-type label for a selected sheet tile. */
export interface ContentDetailSheetTypeInput {
  previewType?: string;
  seasonNumber?: number | null;
  episodeNumber?: number | null;
  feedItemType?: string;
  itemId?: string;
}

/** Inputs used to resolve the main title for a selected sheet tile. */
export interface ContentDetailSheetTitleInput extends ContentDetailSheetTypeInput {
  title?: string;
  episodeTitle?: string;
}

function isSheetEpisode(item: ContentDetailSheetTypeInput): boolean {
  const previewTypeLower: string | undefined =
    item.previewType !== undefined ? item.previewType.toLowerCase() : undefined;
  const feedTypeLower: string | undefined =
    item.feedItemType !== undefined
      ? item.feedItemType.toLowerCase()
      : undefined;
  return (
    feedTypeLower === CONTENT_TYPE_EPISODE ||
    previewTypeLower === CONTENT_TYPE_EPISODE ||
    (typeof item.seasonNumber === "number" && item.seasonNumber > 0) ||
    (typeof item.episodeNumber === "number" && item.episodeNumber > 0)
  );
}

/** Resolves the sheet's content-type-specific main title. */
export function getSheetMainTitle(item: ContentDetailSheetTitleInput): string {
  const title: string = item.title !== undefined ? item.title.trim() : "";
  if (!isSheetEpisode(item)) {
    return title;
  }

  const episodeTitle: string =
    item.episodeTitle !== undefined ? item.episodeTitle.trim() : "";
  const hasSeason: boolean =
    typeof item.seasonNumber === "number" && item.seasonNumber > 0;
  const hasEpisode: boolean =
    typeof item.episodeNumber === "number" && item.episodeNumber > 0;
  let prefix: string = "";
  if (hasSeason && hasEpisode) {
    prefix =
      "S" + String(item.seasonNumber) + ":E" + String(item.episodeNumber);
  } else if (hasEpisode) {
    prefix = "E" + String(item.episodeNumber);
  } else if (hasSeason) {
    prefix = "S" + String(item.seasonNumber);
  }
  if (prefix !== "" && episodeTitle !== "") {
    return prefix + " - " + episodeTitle;
  }
  if (prefix !== "") {
    return prefix;
  }
  return episodeTitle !== "" ? episodeTitle : title;
}

/**
 * Resolves the localized sheet type label from the available tile and preview
 * metadata. Collection identity takes precedence over episodic metadata.
 */
export function getContentDetailSheetTypeLabel(
  item: ContentDetailSheetTypeInput
): string {
  const previewTypeLower: string | undefined =
    item.previewType !== undefined ? item.previewType.toLowerCase() : undefined;
  const feedTypeLower: string | undefined =
    item.feedItemType !== undefined
      ? item.feedItemType.toLowerCase()
      : undefined;
  if (
    feedTypeLower === CONTENT_TYPE_SERIES ||
    (item.itemId !== undefined &&
      item.itemId.startsWith(UNIFIED_COLLECTION_ID_PREFIX)) ||
    previewTypeLower === CONTENT_TYPE_SERIES ||
    previewTypeLower === CONTENT_TYPE_UNIFIED_COLLECTION
  ) {
    return getContentTypeLabel(CONTENT_TYPE_SERIES);
  }

  if (
    feedTypeLower === CONTENT_TYPE_EPISODE ||
    previewTypeLower === CONTENT_TYPE_EPISODE ||
    isSheetEpisode(item)
  ) {
    return getContentTypeLabel(CONTENT_TYPE_EPISODE);
  }

  if (
    feedTypeLower === CONTENT_TYPE_MOVIE ||
    previewTypeLower === CONTENT_TYPE_MOVIE
  ) {
    return getContentTypeLabel(CONTENT_TYPE_MOVIE);
  }

  return item.previewType !== undefined && item.previewType.length > 0
    ? item.previewType.charAt(0).toUpperCase() +
        item.previewType.slice(1).toLowerCase()
    : "";
}

/**
 * Resolved season-picker label for a `seasonOrYear` value.
 *
 * `grouping` comes from the episode-guide `SeasonsData.grouping`
 * discriminator; when the response omits it, values at or above
 * {@link MIN_YEAR_GROUPING_VALUE} are treated as years. Values below
 * {@link EXTRA_SEASON_THRESHOLD} render as "Extra".
 *
 * @param seasonOrYear - Season number, year, or a negative extras marker
 * @param grouping - "season" | "year" from the service, when known
 * @returns The localized picker label
 */
export function getSeasonLabel(
  seasonOrYear: number,
  grouping?: string
): string {
  if (seasonOrYear < EXTRA_SEASON_THRESHOLD) {
    return Locale.getString(LocaleKeys.CONTENT_EPISODE_GUIDE_TAB_EXTRA);
  }
  const isYear: boolean =
    grouping === "year" ||
    (grouping === undefined && seasonOrYear >= MIN_YEAR_GROUPING_VALUE);
  const key: string = isYear
    ? LocaleKeys.CONTENT_EPISODE_GUIDE_TAB_YEAR
    : LocaleKeys.CONTENT_EPISODE_GUIDE_TAB_SEASON;
  return interpolateLocaleTemplate(Locale.getString(key), String(seasonOrYear));
}

/**
 * Resolved "S5 E14" card metadata segment, or `undefined` when either half is
 * missing — Android drops the whole segment rather than rendering a partial
 * one (`buildEpisodeGuideMetadata`).
 */
export function formatSeasonEpisode(
  seasonOrYear: number | undefined,
  episodeNumber: number | undefined
): string | undefined {
  if (seasonOrYear === undefined || episodeNumber === undefined) {
    return undefined;
  }
  return interpolateLocaleTemplate(
    Locale.getString(LocaleKeys.CONTENT_ACTION_SUFFIX_SEASON_EPISODE),
    String(seasonOrYear),
    String(episodeNumber)
  );
}

/** Resolved "Next Airing: …" line for a future-airing title. */
export function formatNextAiring(airingDateTime: string): string {
  return interpolateLocaleTemplate(
    Locale.getString(LocaleKeys.CONTENT_NEXT_AIRING),
    airingDateTime
  );
}

/** Resolved "Watch on Netflix" caption under a focused provider tile. */
export function formatWatchOnProvider(providerName: string): string {
  return interpolateLocaleTemplate(
    Locale.getString(LocaleKeys.CONTENT_WATCH_ON_PROVIDER),
    providerName
  );
}

/**
 * Resolved badge label for a video resolution, or `undefined` for a value the
 * screen does not badge. Mirrors Android's `VideoResolution.toDisplayText`.
 */
export function getVideoResolutionLabel(
  videoResolution: string | undefined
): string | undefined {
  switch (videoResolution) {
    case "hd":
      return Locale.getString(LocaleKeys.CONTENT_BADGE_LABEL_HD);
    case "sd":
      return Locale.getString(LocaleKeys.CONTENT_BADGE_LABEL_SD);
    case "uhd":
      return Locale.getString(LocaleKeys.CONTENT_BADGE_LABEL_UHD);
    default:
      return undefined;
  }
}

/** Resolved "NEW" carousel badge. */
export function getNewBadgeLabel(): string {
  return Locale.getString(LocaleKeys.CONTENT_BADGE_LABEL_NEW);
}

/** Resolved "LIVE" carousel badge. */
export function getLiveBadgeLabel(): string {
  return Locale.getString(LocaleKeys.CONTENT_BADGE_LABEL_LIVE);
}

/** Resolved "Retry" button label for the screen-level and section-level errors. */
export function getRetryLabel(): string {
  return Locale.getString(LocaleKeys.ERROR_GENERIC_RETRY);
}

/** Resolved generic "Failed to load content" section error message. */
export function getSectionErrorMessage(): string {
  return Locale.getString(LocaleKeys.ERROR_GENERIC_FAILED_TO_LOAD);
}

/** Resolved "No actions available" screen error (Android `loadDetailsScreenFlow`). */
export function getNoActionsAvailableMessage(): string {
  return Locale.getString(LocaleKeys.CONTENT_NO_ACTIONS_AVAILABLE);
}

/** Resolved empty-state message for the Episodes section. */
export function getEpisodesEmptyMessage(): string {
  return Locale.getString(LocaleKeys.CONTENT_EPISODES_EMPTY_MESSAGE);
}

/** Resolved empty-state message for the Upcoming section. */
export function getUpcomingEmptyMessage(): string {
  return Locale.getString(LocaleKeys.CONTENT_UPCOMING_EMPTY_MESSAGE);
}

/** Resolved empty-state message for the Cast section. */
export function getCastEmptyMessage(): string {
  return Locale.getString(LocaleKeys.CONTENT_CAST_EMPTY_MESSAGE);
}

/**
 * Resolved empty-state message for the You May Also Like section. Reuses the
 * generic "Nothing to show here" string — recommendations have no
 * feature-specific copy, and Android has no live service for them at all.
 */
export function getMayAlsoLikeEmptyMessage(): string {
  return Locale.getString(LocaleKeys.SEE_MORE_EMPTY_MESSAGE);
}
